'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Hide, Show } from '@iguanads/icons/one-color'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  Input,
  InputControl,
  InputPrefix,
  Loading,
  toast,
} from '@iguanads/react'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import authService from '@/api/auth.service'

const errorMapping: { [key: string]: string } = {
  'auth/invalid-email': 'You have entered an invalid e-mail or password',
  'auth/user-not-found': 'User not found!',
  'auth/wrong-password': 'You have entered an invalid e-mail or password',
  'auth/weak-password':
    'Weak password, password must to be 6 characters or more',
  'auth/email-already-in-use': 'E-mail already in use',
  'auth/too-many-requests':
    'Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later',
  'auth/operation-not-allowed': 'Server error, please try again later.',
  'auth/user-disabled': 'Account disabled by Administrator',
  'auth/invalid-action-code': 'Your request could not be verified',
}

const forgotPasswordFormSchema = z.object({
  code: z.string(),
  password: z.string().min(1, 'Password is required'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordFormSchema>

interface Props {
  oobCode: string
}

export function NewPassword({ oobCode }: Props) {
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      code: oobCode || '',
      password: '',
    },
  })

  const { mutateAsync: forgotPasswordAsync, isPending: isLoading } =
    useMutation({
      mutationFn: authService.newPassword,
    })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    if (!data.code) {
      toast({
        title: 'Something went wrong',
        message: 'Your request could not be verified',
        actionButton: false,
      })

      return
    }

    try {
      await forgotPasswordAsync({ ...data })

      toast({
        type: 'success',
        message: 'Sign in with your new password',
        title: 'Password updated',
        actionButton: false,
      })

      navigate('/sign-in')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const getErrorMessage = error?.message
        ? error.message.match(/\(([^)]+)\)/)
        : error.code.match(/\(([^)]+)\)/)

      toast({
        title: 'Something went wrong',
        message: errorMapping[getErrorMessage[1] || error.code],
        actionButton: false,
      })
    }
  }

  return (
    <>
      <Heading className="text-md text-grayscale-darkest dark:text-grayscale-white pb-4 text-center font-bold md:!text-lg">
        Forgot password
      </Heading>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex w-full flex-col items-center gap-4"
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="text-grayscale-darkest dark:text-grayscale-white dark:bg-grayscale-darkest bg-[#f6f6f6]">
                  New Password
                </FormLabel>
                <FormControl>
                  <Input>
                    <InputControl
                      type={showPassword ? 'text' : 'password'}
                      disabled={isLoading}
                      {...field}
                    />
                    <InputPrefix className="flex">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setShowPassword((state) => !state)}
                        className="px-0"
                        disabled={isLoading}
                      >
                        {showPassword ? <Hide /> : <Show />}
                      </Button>
                    </InputPrefix>
                  </Input>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isLoading || isLoading}
            className="mb-6 w-full px-12"
          >
            {isLoading && <Loading size={2} color="white" className="mr-1" />}
            Continue
          </Button>
        </form>
      </Form>
    </>
  )
}
